import React, { useState, useEffect } from 'react'
import * as XLSX from 'xlsx'
import { Modal, Button, Form, Table, Alert, ProgressBar } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import AxiosControl from '../axios/AxiosControl'
import { useAuth } from '../auth/useAuth'

const Axios = AxiosControl()

// Con listados grandes (iluminacion: ~24.000 filas) la vista previa solo pinta las primeras filas
const PREVIEW_LIMIT = 200

const normalizeDepartments = (departments = []) => {
    return [...new Set(
        departments
            .map(department => String(department || '').trim().toUpperCase())
            .filter(Boolean)
    )]
}

export default function ImportProductsModal({ show, onHide, onImportComplete }) {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [rows, setRows] = useState([])
    const [error, setError] = useState('')
    const [importProgress, setImportProgress] = useState(0)
    const [importTotal, setImportTotal] = useState(0)
    const [importDone, setImportDone] = useState(false)
    const { clientauth } = useAuth()

    useEffect(() => {
        if (!show) {
            setImportProgress(0)
            setImportTotal(0)
            setImportDone(false)
            setError('')
        }
    }, [show])

    const parseExcel = async (file) => {
        try {
            setLoading(true)
            setError('')

            const data = await file.arrayBuffer()
            const workbook = XLSX.read(data)
            const worksheet = workbook.Sheets[workbook.SheetNames[0]]

            const json = XLSX.utils.sheet_to_json(worksheet, {defval: ''})
            const defaultDepartments = normalizeDepartments(clientauth?.departments || [])

            const products = json.filter(row => row['Referencia']).map(row => ({
                    reference:
                        String(
                            row['Referencia'] || ''
                        ).trim(),
                    name:
                        String(
                            row['Descripción'] || ''
                        ).trim(),
                    description:
                        String(
                            row['Descripción'] || ''
                        ).trim(),
                    brand:
                        String(
                            row['Marca'] || ''
                        ).trim(),
                    group:
                        String(
                            row['Grupo'] || ''
                        ).trim(),
                    subgroup:
                        String(
                            row['SubGrupo'] || ''
                        ).trim(),
                    families: [
                        row['Fam.1'] || row['Fam. 1'],
                        row['Fam.2'] || row['Fam. 2'],
                        row['Fam.3'] || row['Fam. 3'],
                        row['Fam.4'] || row['Fam. 4'],
                    ].filter(Boolean).map(item => String(item).trim()),
                    fam1: String(row['Fam.1'] || row['Fam. 1'] || '').trim(),
                    fam2: String(row['Fam.2'] || row['Fam. 2'] || '').trim(),
                    fam3: String(row['Fam.3'] || row['Fam. 3'] || '').trim(),
                    fam4: String(row['Fam.4'] || row['Fam. 4'] || '').trim(),
                    articulo: String(row['Artículo'] || row['ARTICULO'] || row['articulo'] || '').trim(),
                    // Plantilla de atributos (p. ej. "Tipo LUZ"); si no viene, el backend usa Fam. 2
                    templateType: String(row['Tipo LUZ'] || row['Plantilla'] || '').trim(),
                    // Departamento del fichero si lo trae; si no, el primero del usuario
                    departments: String(row['Departamento'] || '').trim()
                        ? normalizeDepartments(String(row['Departamento']).split(/[,;]/))
                        : defaultDepartments.slice(0, 1),
                    attributes: {}
                }))

            setRows(products)
        } catch (err) {
            console.error(err)
            setError(t('admin.products.importParseError'))
        } finally {
            setLoading(false)
        }
    }

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0]

        if (!file) {
            return
        }

        await parseExcel(file)
    }

    const handleImport = async () => {
        const BATCH = 100

        setLoading(true)
        setError('')
        setImportProgress(0)
        setImportTotal(rows.length)
        setImportDone(false)

        let totalCreated = 0
        let totalSkipped = 0
        let totalErrors = 0

        for (let i = 0; i < rows.length; i += BATCH) {
            const batch = rows.slice(i, i + BATCH)

            try {
                const { data } = await AxiosControl().post(
                    '/api/product/import',
                    { rows: batch },
                    { headers: { auth: localStorage.getItem('jwt') } }
                )
                const d = data?.data
                if (d?.summary) {
                    totalCreated += d.summary.created || 0
                    totalSkipped += d.summary.unchanged || 0
                    totalErrors += d.summary.errors || 0
                }
            } catch (err) {
                totalErrors += batch.length
            }

            setImportProgress(Math.min(i + BATCH, rows.length))
        }

        setError(t('admin.products.importComplete', { created: totalCreated, skipped: totalSkipped, errors: totalErrors }))
        setImportDone(true)
        setLoading(false)
        if (onImportComplete) onImportComplete()
    }

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
        >

            <Modal.Header closeButton>
                <Modal.Title>
                    {t('admin.products.import')}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form.Group>
                    <Form.Label>
                        {t('admin.products.importFile')}
                    </Form.Label>
                    <Form.Control
                        type="file"
                        accept=".xls,.xlsx"
                        onChange={handleFileChange}
                    />
                </Form.Group>

                {loading && importTotal > 0 && (
                    <div className="mt-3 mb-3">
                        <ProgressBar
                            now={(importProgress / importTotal) * 100}
                            label={`${importProgress} / ${importTotal}`}
                            striped
                            animated
                        />
                        <div className="mt-2 text-muted small">
                            {t('admin.products.importing')}
                        </div>
                    </div>
                )}

                {error && !loading &&
                    <Alert
                        variant={importDone ? 'success' : 'danger'}
                        className="mt-3"
                    >
                        {error}
                    </Alert>
                }

                {!!rows.length &&
                    <>
                        <div className="mt-4 mb-2">
                            <strong>
                                {t('admin.products.productsDetected')}:
                            </strong>{' '}
                            {rows.length}
                        </div>
                        <div
                            style={{
                                maxHeight: '500px',
                                overflow: 'auto'
                            }}
                        >
                            <Table striped bordered hover responsive>
                                <thead>
                                    <tr>
                                        <th>{t('admin.products.reference')}</th>
                                        <th>{t('admin.products.description')}</th>
                                        <th>{t('admin.products.brand')}</th>
                                        <th>{t('admin.products.group')}</th>
                                        <th>{t('admin.products.subgroup')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.slice(0, PREVIEW_LIMIT).map((product, index) => (
                                        <tr key={index}>
                                            <td>{product.reference}</td>
                                            <td>{product.name}</td>
                                            <td>{product.brand}</td>
                                            <td>{product.group}</td>
                                            <td>{product.subgroup}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    </>
                }
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="secondary"
                    onClick={onHide}
                >
                    {t('global.close')}
                </Button>
                <Button
                    variant="primary"
                    disabled={
                        loading ||
                        !rows.length
                    }
                    onClick={handleImport}
                >
                    {loading
                        ? t('admin.products.importing')
                        : t('admin.products.import')
                    }
                </Button>
            </Modal.Footer>
        </Modal>
    )
}
